import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Mail, LifeBuoy, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/apiClient";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CustomBlocks from "@/components/site/CustomBlocks";

const field = "w-full bg-transparent border-b border-[#E5DFD3] rounded-none px-0 py-3 text-[#1A201D] placeholder:text-[#9A8C73]/60 focus:border-[#2C3531] focus:outline-none transition-colors";

export default function Contact({ previewSite }) {
  const outlet = useOutletContext();
  const site = previewSite || outlet?.site;
  const p = site?.pages?.contact;
  const [f, setF] = useState({ name: "", email: "", phone: "", specialty: "", business_model: "", message: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/contact", f);
      toast.success("Thanks — we'll be in touch shortly.");
      setF({ name: "", email: "", phone: "", specialty: "", business_model: "", message: "" });
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  if (!p) return null;
  return (
    <div data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-28 grid lg:grid-cols-2 gap-16">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#9A8C73] mb-5">{p.hero.eyebrow}</p>
        <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight text-[#1A201D] mb-6">{p.hero.title}</h1>
        <p className="text-lg text-[#4A5550] leading-relaxed mb-12">{p.hero.subtitle}</p>
        <div className="space-y-6">
          <a href={`mailto:${p.channels.sales_email}`} className="flex items-center gap-4 group">
            <span className="w-11 h-11 rounded-full bg-[#F3EFE7] flex items-center justify-center"><Mail className="w-5 h-5 text-[#9A8C73]" strokeWidth={1.5} /></span>
            <div><p className="text-xs uppercase tracking-wider text-[#9A8C73]">Sales & Inquiries</p><p className="font-medium text-[#1A201D] group-hover:text-[#9A8C73] transition-colors">{p.channels.sales_email}</p></div>
          </a>
          <a href={`mailto:${p.channels.support_email}`} className="flex items-center gap-4 group">
            <span className="w-11 h-11 rounded-full bg-[#F3EFE7] flex items-center justify-center"><LifeBuoy className="w-5 h-5 text-[#9A8C73]" strokeWidth={1.5} /></span>
            <div><p className="text-xs uppercase tracking-wider text-[#9A8C73]">Practitioner Support</p><p className="font-medium text-[#1A201D] group-hover:text-[#9A8C73] transition-colors">{p.channels.support_email}</p></div>
          </a>
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-full bg-[#F3EFE7] flex items-center justify-center"><Clock className="w-5 h-5 text-[#9A8C73]" strokeWidth={1.5} /></span>
            <div><p className="text-xs uppercase tracking-wider text-[#9A8C73]">Live Chat</p><p className="font-medium text-[#1A201D]">{p.channels.hours}</p></div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
        <input data-testid="contact-name" className={field} placeholder="Your full name" value={f.name} onChange={(e) => upd("name", e.target.value)} required />
        <input data-testid="contact-email" type="email" className={field} placeholder="Email address" value={f.email} onChange={(e) => upd("email", e.target.value)} required />
        <input data-testid="contact-phone" className={field} placeholder="Phone number" value={f.phone} onChange={(e) => upd("phone", e.target.value)} />
        <div>
          <Select value={f.specialty} onValueChange={(v) => upd("specialty", v)}>
            <SelectTrigger data-testid="contact-specialty" className="border-0 border-b border-[#E5DFD3] rounded-none px-0 shadow-none focus:ring-0"><SelectValue placeholder="Aesthetic specialty" /></SelectTrigger>
            <SelectContent>{p.specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Select value={f.business_model} onValueChange={(v) => upd("business_model", v)}>
            <SelectTrigger data-testid="contact-business" className="border-0 border-b border-[#E5DFD3] rounded-none px-0 shadow-none focus:ring-0"><SelectValue placeholder="Current business model" /></SelectTrigger>
            <SelectContent>{p.business_models.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <textarea data-testid="contact-message" className={`${field} resize-none`} rows={4} placeholder="Your message" value={f.message} onChange={(e) => upd("message", e.target.value)} />
        <button type="submit" disabled={loading} data-testid="contact-submit"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#2C3531] text-[#FDFBF7] font-semibold hover:scale-105 transition-transform duration-300 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Inquiry"}
        </button>
      </form>
      </div>
      <CustomBlocks blocks={p.blocks} />
    </div>
  );
}
